import React, { useState } from 'react';
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertTriangle, Printer } from 'lucide-react';
import { toast } from 'sonner';
import DriverAutocomplete from '@/components/documents/DriverAutocomplete';

const MISSING_FIELD_LABELS = {
  driver_name: 'Nama Sopir',
  vehicle_plate: 'Plat Kendaraan',
  delivery_address: 'Alamat Pengiriman',
  delivery_date: 'Tanggal Kirim',
};

/**
 * Dialog kecil minta info pengiriman (sopir/plat/alamat/tanggal) untuk batch
 * yang belum lengkap. Muncul otomatis sebelum mencetak SO/SJ/Invoice.
 *
 * Props:
 *   - batch: batch object (untuk auto-fill)
 *   - tx: transaksi induk (fallback source)
 *   - missing: array missing_fields dari backend
 *   - docLabel: label dokumen ('Surat Jalan' / 'Sales Order' / 'Invoice')
 *   - onSave({driver, plate, address, deliveryDate}): dipanggil saat Simpan & Cetak
 */
export default function DeliveryPromptDialog({ batch, tx, missing, docLabel, onSave }) {
  const [driver, setDriver] = useState(batch.driver_name || tx?.driver_name || '');
  const [plate, setPlate] = useState(batch.vehicle_plate || tx?.vehicle_plate || '');
  const [address, setAddress] = useState(
    batch.delivery_address || tx?.delivery_address || tx?.customer_address || ''
  );
  const [deliveryDate, setDeliveryDate] = useState(
    batch.delivery_date || tx?.delivery_date || new Date().toISOString().slice(0, 10)
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!driver.trim() || !plate.trim() || !address.trim() || !deliveryDate) {
      toast.error('Semua field pengiriman wajib diisi');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        driver: driver.trim(),
        plate: plate.trim().toUpperCase(),
        address: address.trim(),
        deliveryDate,
      });
    } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-md" data-testid="delivery-prompt-dialog">
      <DialogHeader>
        <DialogTitle>Info Pengiriman — Batch #{batch.batch_index}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <div className="rounded-md border bg-amber-50 border-amber-300 p-2 text-xs text-amber-900 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">
              Untuk mencetak <b>{docLabel}</b>, info pengiriman batch ini wajib diisi lebih dahulu.
            </div>
            {missing?.length > 0 && (
              <div className="text-[11px] mt-0.5">
                Kurang: {missing.map((f) => MISSING_FIELD_LABELS[f] || f).join(', ')}
              </div>
            )}
          </div>
        </div>
        <DriverAutocomplete
          driver={driver}
          setDriver={setDriver}
          plate={plate}
          setPlate={setPlate}
          required
          testIdPrefix="delivery-prompt-ac"
        />
        <div>
          <Label className="text-xs">Alamat Pengiriman <span className="text-rose-600">*</span></Label>
          <Textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Alamat lengkap tujuan pengiriman"
            rows={2}
            className="text-sm"
            data-testid="delivery-prompt-address"
          />
        </div>
        <div>
          <Label className="text-xs">Tanggal Kirim <span className="text-rose-600">*</span></Label>
          <Input
            type="date"
            value={deliveryDate}
            onChange={(e) => setDeliveryDate(e.target.value)}
            data-testid="delivery-prompt-date"
          />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="delivery-prompt-save">
          <Printer className="h-3.5 w-3.5 mr-1" /> {saving ? 'Menyimpan...' : 'Simpan & Cetak'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
